import { useTranslation } from 'react-i18next'
import { Input } from '@arabquiz/ui'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'

type AdminSearchBarProps = {
  value: string
  onChange: (next: string) => void
  placeholder: string
}

/**
 * Leading-magnifier search input shared by the admin tables. Controlled by the
 * parent (which debounces the value into the actual query input), matching the
 * Question Bank browser's search affordance.
 */
export function AdminSearchBar({ value, onChange, placeholder }: AdminSearchBarProps) {
  return (
    <div className="relative w-full lg:max-w-md">
      <Search
        className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-brand-400"
        aria-hidden="true"
      />
      <Input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="ps-9"
      />
    </div>
  )
}

type AdminPaginationProps = {
  page: number
  pageCount: number
  onPrev: () => void
  onNext: () => void
}

/** Logical-chevron pager (arrows flip under RTL) shared by the admin tables. */
export function AdminPagination({ page, pageCount, onPrev, onNext }: AdminPaginationProps) {
  const { t } = useTranslation('common')
  if (pageCount <= 1) return null
  return (
    <nav
      aria-label={t('page')}
      className="mt-8 flex items-center justify-center gap-3"
    >
      <button
        type="button"
        onClick={onPrev}
        disabled={page <= 1}
        aria-label={t('back')}
        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-brand-200 text-brand-700 hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
      </button>
      <span className="text-sm font-semibold text-brand-700 tabular-nums">
        {t('pageOf', { page, total: pageCount })}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={page >= pageCount}
        aria-label={t('next')}
        className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-brand-200 text-brand-700 hover:bg-brand-50 disabled:opacity-40 disabled:hover:bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
      </button>
    </nav>
  )
}

/** Inline red error banner shared by the admin tables. */
export function AdminErrorBanner({ message }: { message: string }) {
  return (
    <div
      className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
      role="alert"
    >
      {message}
    </div>
  )
}

/** Dashed empty/no-results panel shared by the admin tables. */
export function AdminEmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-3xl border border-dashed border-brand-200 bg-white/60 px-6 py-14 text-center">
      <p className="text-brand-700 text-lg font-semibold">{message}</p>
    </div>
  )
}
